import React, { memo, useEffect, useState } from "react";

import {
	Button,
	TextField,
	DialogActions,
	Typography,
	FormControlLabel,
	Checkbox,
	RadioGroup,
	Radio,
	Paper,
	List,
	ListItem,
	ListItemText,
	ClickAwayListener,
	Popper
} from "@mui/material";
import { Dialog } from "orion-components/CBComponents";

import { getTranslation, Translate } from "orion-components/i18n";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { shapeService } from "client-app-core";
import { updateDevice } from "../../../SharedActions/deviceProfileActions";

const propTypes = {
	device: PropTypes.object.isRequired,
	close: PropTypes.func,
	update: PropTypes.func,
	dir: PropTypes.string,
	entityType: PropTypes.string // Added entityType prop
};

const DeviceEditDialog = ({ device, close, update, dir, entityType }) => {
	const dispatch = useDispatch();
	const dialog = useSelector(state => state.appState?.dialog?.openDialog || "");
	const deviceTypes = useSelector(state => state?.globalData?.deviceTypes);
	const type = device ? device.entityData.properties.type : "";
	const deviceId = device ? device.id : "";

	const [name, setName] = useState(device ? device.entityData.properties.name : "");
	const [description, setDescription] = useState(device ? device.entityData.properties.description : "");
	const [error, setError] = useState({
		name: ""
	});

	// Ticket-specific states
	const [accountability, setAccountability] = useState(false);
	const [badgeDirection, setBadgeDirection] = useState("ingress");
	const [fromSectorName, setFromSectorName] = useState("");
	const [fromSectorId, setFromSectorId] = useState("");

	const [toSectorName, setToSectorName] = useState("");
	const [toSectorId, setToSectorId] = useState("");

	const [accountSectorName, setAccountSectorName] = useState("");
	const [accountSectorId, setAccountSectorId] = useState("");

	const [anchorEl, setAnchorEl] = useState(null);
	const [activeInput, setActiveInput] = useState("");
	const [filteredSectors, setFilteredSectors] = useState([]);
	const [sectors, setSectors] = useState([]);
	const [accountSectorError, setAccountSectorError] = useState("");
	const [isSiteEntryPoint, setIsSiteEntryPoint] = useState(false);
	const [isSiteExitPoint, setIsSiteExitPoint] = useState(false);

	const handleClose = () => {
		close();
	};

	useEffect(() => {
		const types = ["polygon", "rectangle", "circle"];
		shapeService.getMyShapes({ types }, (err, result) => {
			if (err) {
				console.log(err, result);
			}
			if (result?.length > 0) {
				const sortedSectors = result.toSorted((a, b) => {
					const nameA = a.entityData?.properties?.name?.toLowerCase() || "";
					const nameB = b.entityData?.properties?.name?.toLowerCase() || "";
					return nameA.localeCompare(nameB);
				});
				setSectors(sortedSectors);
			}
		});
	}, []);

	useEffect(() => {
		if (accountability) {
			// When turning ON accountability, clear everything
			setFromSectorId(null);
			setToSectorId(null);
			setFromSectorName("");
			setToSectorName("");
			setIsSiteEntryPoint(false);
			setIsSiteExitPoint(false);
		}
	}, [accountability]);
	useEffect(() => {
		if (device?.config) {
			setAccountability(device.config.accountability);
			setAccountSectorId(device.config.accountabilitySector);
			setBadgeDirection(device?.config.badgeDirection);
			setIsSiteEntryPoint(device?.config.isSiteEntryPoint ?? false);
			setIsSiteExitPoint(device?.config.isSiteExitPoint ?? false);
			if (sectors.length > 0) {
				sectors.forEach(sector => {
					if (sector.id === device?.config?.accountabilitySector) {
						setAccountSectorId(sector.id);
						setAccountSectorName(sector.entityData.properties.name);
					}
					if (sector.id === device?.config?.fromSector) {
						setFromSectorId(sector.id);
						setFromSectorName(sector.entityData.properties.name);
					}
					if (sector.id === device?.config?.toSector) {
						setToSectorId(sector.id);
						setToSectorName(sector.entityData.properties.name);
					}
				});
			}
		}
	}, [device, sectors]);

	const handleNameChange = event => {
		if (event.target.value.length > 50) {
			setError({
				...error,
				name: getTranslation("global.profiles.deviceProfile.Dialog.errorText.lessThanFifty")
			});
			return;
		}
		setName(event.target.value);
		setError({ ...error, name: "" });
	};

	const handleDescriptionChange = event => {
		setDescription(event.target.value);
	};

	const handleCancel = () => {
		setName(device ? device.entityData.properties.name : "");
		setDescription(device ? device.entityData.properties.description : "");
		handleClose();
	};

	const handleSave = () => {
		if (!name) {
			setError({
				...error,
				name: getTranslation("global.profiles.deviceProfile.Dialog.errorText.noName")
			});
			return;
		}
		if (deviceTypes?.[device?.deviceType]?.type === "cardReader") {
			if (accountability) {
				if (!accountSectorId) {
					setAccountSectorError(getTranslation("global.profiles.deviceProfile.Dialog.required"));
					return;
				} else {
					setAccountSectorError("");
				}
			}
			const config = accountability
				? {
						accountability,
						accountabilitySector: accountSectorId,
						isSiteEntryPoint: false,
						isSiteExitPoint: false,
						fromSector: null,
						toSector: null,
						badgeDirection: null
					}
				: {
						accountability,
						badgeDirection,
						fromSector: badgeDirection === "ingress" && isSiteEntryPoint ? null : fromSectorId,
						toSector: badgeDirection === "egress" && isSiteExitPoint ? null : toSectorId,
						isSiteEntryPoint: badgeDirection === "egress" ? false : isSiteEntryPoint,
						isSiteExitPoint: badgeDirection === "ingress" ? false : isSiteExitPoint
					};
			dispatch(updateDevice(device.id, { config }, false));
		}
		const updatedProps = {
			...device.entityData.properties,
			name,
			description
		};

		const entityData = {
			...device.entityData,
			entityType,
			properties: {
				...updatedProps
			}
		};
		dispatch(updateDevice(device.id, entityData, true));

		handleClose();
	};

	const handleSectorChange = (e, field) => {
		const value = e.target.value;
		setAnchorEl(e.currentTarget);

		const matches = sectors.filter(sector => {
			const name = sector?.entityData?.properties?.name || "";
			return name.toLowerCase().includes(value.toLowerCase());
		});

		if (field === "from") setFromSectorName(value);
		else if (field === "to") setToSectorName(value);
		else setAccountSectorName(value);

		setFilteredSectors(matches);
	};

	const handleSelectSector = sector => {
		const sectorName = sector.entityData?.properties?.name || "";
		const sectorId = sector.id;

		if (activeInput === "from") {
			setFromSectorName(sectorName);
			setFromSectorId(sectorId);
		} else if (activeInput === "to") {
			setToSectorName(sectorName);
			setToSectorId(sectorId);
		} else {
			setAccountSectorName(sectorName);
			setAccountSectorId(sectorId);
			setAccountSectorError("");
		}

		setAnchorEl(null);
	};

	const dialogStyles = {
		dialog: {
			maxWidth: 500
		}
	};

	const textFieldStyles = {
		marginBottom: "2rem",
		backgroundColor: "#2C2D2F", // Force styling in Map app
		input: { textOverflow: "ellipsis" }
	};

	const actions = [
		<Button style={{ color: "#B2B6BB" }} variant="text" onClick={handleCancel} key="cancel-action-button" data-testid="device-edit-cancel-button">
			{getTranslation("global.profiles.deviceProfile.Dialog.cancel")}
		</Button>,
		<Button color="primary" variant="text" onClick={handleSave} key="confirm-action-button" data-testid="device-edit-save-button">
			{getTranslation("global.profiles.deviceProfile.Dialog.confirm")}
		</Button>
	];

	const dialogTitle = [
		<Typography sx={{ fontWeight: "400", fontSize: "22px" }} key="dialog-title">
			{getTranslation("global.profiles.deviceProfile.Dialog.editAcc")}
		</Typography>
	];

	const dialogActions = () => {
		return <DialogActions data-testid="orion-components-profiles-device-profile-components-device-edit-dialog-dialog-actions">{actions}</DialogActions>;
	};

	const listAllSectors = (e, field) => {
		setAnchorEl(e.currentTarget);
		setActiveInput(field);
		setFilteredSectors(sectors); // show full list initially
	};

	return (
		<Dialog
			data-testid="device-edit-dialog"
			open={dialog === "deviceEditDialog"}
			paperPropStyles={dialogStyles.dialog}
			dialogContentStyles={{
				paddingTop: "25px",
				paddingBottom: "25px",
				borderTop: "none",
				borderBottom: "none"
			}}
			titlePropStyles={{ marginBottom: "1.5rem" }}
			title={dialogTitle}
			actions={true}
			DialogActionsFunction={dialogActions}
		>
			<div>
				<TextField
					data-testid="device-name-input"
					id="device-name"
					fullWidth={true}
					variant="standard"
					label={getTranslation("global.profiles.deviceProfile.Dialog.accName")}
					value={name}
					onChange={handleNameChange}
					sx={textFieldStyles}
					InputProps={{ style: textFieldStyles.input }}
					errorText={error.name}
					InputLabelProps={{
						style: {
							color: "#646465",
							transformOrigin: dir && dir == "rtl" ? "top right" : "top left",
							left: "unset"
						}
					}}
					autoFocus={true}
				/>
				<TextField
					data-testid="device-description-input"
					id="device-description"
					fullWidth={true}
					variant="standard"
					label={getTranslation("global.profiles.deviceProfile.Dialog.desc")}
					value={description}
					onChange={handleDescriptionChange}
					sx={textFieldStyles}
					InputProps={{ style: textFieldStyles.input }}
					InputLabelProps={{
						style: {
							color: "#646465",
							transformOrigin: dir && dir == "rtl" ? "top right" : "top left",
							left: "unset"
						}
					}}
				/>
				<TextField
					data-testid="device-type-input"
					id="device-type"
					fullWidth={true}
					variant="standard"
					label={getTranslation("global.profiles.deviceProfile.Dialog.type")}
					disabled={true}
					value={type}
					style={textFieldStyles}
					InputProps={{ style: textFieldStyles.input }}
					InputLabelProps={{
						style: {
							color: "#646465",
							transformOrigin: dir && dir == "rtl" ? "top right" : "top left",
							left: "unset"
						}
					}}
				/>
				<TextField
					data-testid="device-id-input"
					id="device-id"
					fullWidth={true}
					variant="standard"
					label={getTranslation("global.profiles.deviceProfile.Dialog.accId")}
					disabled={true}
					value={deviceId}
					style={textFieldStyles}
					InputProps={{ style: textFieldStyles.input }}
					InputLabelProps={{
						style: {
							color: "#646465",
							transformOrigin: dir && dir == "rtl" ? "top right" : "top left",
							left: "unset"
						}
					}}
				/>
				{/* Show additional fields only for cardReader */}
				{deviceTypes?.[device?.deviceType]?.type === "cardReader" && (
					<>
						<FormControlLabel
							control={
								<Checkbox
									data-testid="device-accountability-checkbox"
									checked={accountability}
									onChange={() => setAccountability(!accountability)}
									color="primary"
								/>
							}
							label={getTranslation("global.profiles.deviceProfile.Dialog.accCardReader")}
							style={{ color: "#fff", marginBottom: "1rem" }}
						/>

						{accountability ? (
							<>
								<TextField
									data-testid="device-account-sector-input"
									fullWidth
									variant="standard"
									label={getTranslation("global.profiles.deviceProfile.Dialog.sector")}
									value={accountSectorName}
									onChange={e => {
										handleSectorChange(e, "account");
									}}
									onClick={e => listAllSectors(e, "account")}
									sx={textFieldStyles}
									InputProps={{ style: textFieldStyles.input }}
									InputLabelProps={{
										style: {
											color: "#646465",
											transformOrigin: dir && dir == "rtl" ? "top right" : "top left",
											left: "unset"
										}
									}}
									error={!!accountSectorError}
									helperText={accountSectorError}
								/>
							</>
						) : (
							<>
								<Typography style={{ color: "#fff", marginBottom: "0.5rem" }}>
									<Translate value="global.profiles.deviceProfile.Dialog.badgeDirection" />
								</Typography>
								<RadioGroup
									row
									value={badgeDirection}
									onChange={e => {
										setBadgeDirection(e.target.value);
									}}
									style={{ marginBottom: "1rem" }} data-testid="orion-components-profiles-device-profile-components-device-edit-dialog-radio-group"
								>
									<FormControlLabel
										value="ingress"
										control={<Radio color="primary" data-testid="device-ingress-radio" />}
										label={getTranslation("global.profiles.deviceProfile.Dialog.ingress")}
										style={{ color: "#fff" }}
									/>
									<FormControlLabel
										value="egress"
										control={<Radio color="primary" data-testid="device-egress-radio" />}
										label={getTranslation("global.profiles.deviceProfile.Dialog.egress")}
										style={{ color: "#fff" }}
									/>
								</RadioGroup>
								{!(badgeDirection === "ingress" && isSiteEntryPoint) && (
									<TextField
										data-testid="device-from-sector-input"
										fullWidth
										variant="standard"
										label={getTranslation("global.profiles.deviceProfile.Dialog.fromSector")}
										value={fromSectorName}
										onClick={e => listAllSectors(e, "from")}
										onChange={e => handleSectorChange(e, "from")}
										style={textFieldStyles}
										InputProps={{ style: textFieldStyles.input }}
										InputLabelProps={{
											style: {
												color: "#646465",
												transformOrigin: dir && dir == "rtl" ? "top right" : "top left",
												left: "unset"
											}
										}}
									/>
								)}
								{!(badgeDirection === "egress" && isSiteExitPoint) && (
									<TextField
										data-testid="device-to-sector-input"
										fullWidth
										variant="standard"
										label={getTranslation("global.profiles.deviceProfile.Dialog.toSector")}
										value={toSectorName}
										onClick={e => listAllSectors(e, "to")}
										onChange={e => handleSectorChange(e, "to")}
										style={textFieldStyles}
										InputProps={{ style: textFieldStyles.input }}
										InputLabelProps={{
											style: {
												color: "#646465",
												transformOrigin: dir && dir == "rtl" ? "top right" : "top left",
												left: "unset"
											}
										}}
									/>
								)}

								{badgeDirection === "ingress" && (
									<div>
										<FormControlLabel
											control={
												<Checkbox
													data-testid="device-site-entry-checkbox"
													checked={isSiteEntryPoint}
													onChange={() => {
														setIsSiteEntryPoint(!isSiteEntryPoint);
													}}
													color="primary"
												/>
											}
											label={getTranslation(
												"global.profiles.deviceProfile.Dialog.siteEntryPoint"
											)}
											style={{ color: "#fff" }}
										/>
									</div>
								)}

								{badgeDirection === "egress" && (
									<div>
										<FormControlLabel
											control={
												<Checkbox
													data-testid="device-site-exit-checkbox"
													checked={isSiteExitPoint}
													onChange={() => {
														setIsSiteExitPoint(!isSiteExitPoint);
													}}
													color="primary"
												/>
											}
											label={getTranslation("global.profiles.deviceProfile.Dialog.siteExitPoint")}
											style={{ color: "#fff" }}
										/>
									</div>
								)}
							</>
						)}

						<Popper
							open={!!anchorEl && filteredSectors.length > 0}
							anchorEl={anchorEl}
							placement="bottom-start"
							disablePortal
							style={{ zIndex: 9999, width: "450px" }}
						>
							<ClickAwayListener onClickAway={() => setAnchorEl(null)} data-testid="orion-components-profiles-device-profile-components-device-edit-dialog-click-away-listener">
								<Paper style={{ maxHeight: 200, overflowY: "auto", backgroundColor: "#1F1F21" }}>
									<List data-testid="device-sector-list">
										{filteredSectors.map(sector => {
											const sectorName = sector.entityData?.properties?.name || "Unnamed Sector";
											return (
											<ListItem
												data-testid="device-sector-list-item"
													button
													key={sector.id}
													onClick={() => handleSelectSector(sector)}
													sx={{
														"&:hover": {
															backgroundColor: "#333",
															cursor: "pointer"
														}
													}}
												>
													<ListItemText
														primary={sectorName}
														primaryTypographyProps={{
															variant: "body2",
															sx: {
																color: "#FFFFFF !important",
																textAlign: dir && dir === "rtl" ? "right" : "left"
															}
														}}
													/>
												</ListItem>
											);
										})}
									</List>
								</Paper>
							</ClickAwayListener>
						</Popper>
					</>
				)}
			</div>
		</Dialog>
	);
};

const componentUpdate = (prevProps, nextProps) => {
	if (prevProps.device?.id !== nextProps.device?.id) {
		return false;
	} else {
		return true;
	}
};

DeviceEditDialog.propTypes = propTypes;

export default memo(DeviceEditDialog, componentUpdate);
